import React, { useCallback, useEffect, useState, useContext } from 'react';
import { handleImageSave } from '../../../screens/ImageEditorBatch/utilities';
import { Modal, Typography, Button, IconButton } from '@common-ui/ui';
import { useHistory } from 'react-router';
import CloseIcon from '@mui/icons-material/Close';
import { UserContext } from '../../../context';
import routerPathEnum from '../../../enums/routerPathEnum';
// import { useAppDispatch } from '../../../store/configureStore';
// import { fetchImagePreviewsBySearch } from '../../../store/slices/imagesSlice';

const { IMAGES, REPORT, REPORTS, LOGOUT } = routerPathEnum;

/**
 * A prompt that appears when the user attempts to leave the Image Editor
 * It will ask the user if they want to keep their recent changes or not
 *
 * @param {*} groupImageData - The changes from the current Image Editor session
 *
 * @returns {JSX.Element} - The prompt that appears when the user attempts to leave the Image Editor
 */

export function SavePromptOnExit({ groupImageData }) {
  const [showPrompt, setShowPrompt] = useState(false);
  const [nextLocation, setNextLocation] = useState(null);
  // const dispatch = useAppDispatch();

  const {
    userSettings: { currentOrganization = '' },
  } = useContext(UserContext);

  // const { updateSessionStore } = useContext(SessionContext);

  const history = useHistory();

  // only block the router when they choose to move to one of the following paths
  const paths = [IMAGES, REPORT, REPORTS, LOGOUT];

  useEffect(() => {
    // the router will get blocked when the user enters the Image Editor
    // in order to show the prompt
    history.block((location) => {
      setNextLocation(location);
      if (!paths.some((path) => location.pathname.startsWith(path))) {
        history.block(() => {});
        return true;
      } else {
        setShowPrompt(true);

        // the following comments are for the return value:
        // false means block the router from changing location which allows the prompt to be seen
        // true would allow the router to change location and skip the prompt
        return false;
      }
    });
  }, []);

  // this decorator is used to handle the history.push() call
  // that is made in the handleSave and handleDontSave functions
  function historyDecorator(func) {
    return (...args) => {
      if (func) func(...args);

      history.block(() => {});
      setTimeout(() => {
        history.push(nextLocation);
      }, [2000]);
    };
  }

  const handleSave = useCallback(() => {
    handleImageSave(groupImageData, currentOrganization);

    // dispatch(fetchImagePreviewsBySearch(updateSessionStore));
  }, [nextLocation, currentOrganization]);

  return (
    <Modal open={showPrompt} onClose={() => setShowPrompt(false)}>
      <div style={styles.container}>
        <div style={styles.content}>
          <Typography style={styles.title}>Save Changes</Typography>
          <IconButton onClick={() => setShowPrompt(false)}>
            <CloseIcon style={styles.closeIcon} />
          </IconButton>
        </div>
        <div style={styles.accordions}>
          <Typography pt={1}>Do you want to save all changes before exiting the editor?</Typography>
          <div style={styles.buttons}>
            <Button
              variant="outlined"
              color="secondary"
              style={{ ...styles.button, ...styles.dontSave }}
              onClick={historyDecorator()}
            >
              Discard
            </Button>
            <Button
              style={styles.button}
              variant="contained"
              color="secondary"
              onClick={historyDecorator(handleSave)}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

const styles = {
  title: {
    textAlign: 'left',
    width: '500px',
    fontSize: '22px',
  },

  container: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '554px',
    height: '50vh',
    minHeight: '104px',
    maxHeight: '220px',
    boxShadow: 24,
    opacity: 1,
    padding: '24px',
    background: '#232323',
    border: '2px solid #000',
    color: 'white',
  },

  dontSave: { color: 'white', border: '1px solid rgba(255, 255, 255, 0.5)' },

  content: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 0px',
  },

  closeIcon: {
    justifySelf: 'flex-end',
  },

  accordions: {
    borderTop: '1px solid rgba(255, 255, 255, 0.12)',
    height: 'calc(100% - 60px)',
  },

  button: {
    width: '8rem',
  },

  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'end',
    height: 'calc(100% - 24px)',
    gap: 5,
  },
};
